<template>
    <div class="nc-messenger">
        <template>
            <v-preloader v-if="isMessagesLoading" class="preloader_type_button" size="small" />
            <template v-else>
                <div v-if="!messages.length" class="nc-messenger__empty">Нет сообщений</div>
                <div v-else ref="list" class="nc-messenger__list">
                    <div v-for="msg in messages" :key="msg.id" class="nc-messenger__row">
                        <new-chat-message-event v-if="msg.type === 'event'" :message="msg" />
                        <new-chat-message-media
                            v-else-if="msg.type === 'media'"
                            :message="msg"
                            :chat-id="activeChat && activeChat.id"
                            @open="openViewer"
                        />
                        <new-chat-message-text v-else-if="msg.type === 'text'" :message="msg" />
                    </div>
                </div>
            </template>
        </template>

        <!-- Composer і viewer завжди видимі -->
        <new-chat-composer
            v-if="canCompose"
            ref="composer"
        />
        <new-chat-attachment-viewer
            :attachment="viewerAttachment"
            @close="viewerAttachment = null"
        />
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import api from '@api'
import vPreloader from '../../global/Preloader.vue'
import NewChatMessageEvent from './messages/NewChatMessageEvent.vue'
import NewChatMessageText from './messages/NewChatMessageText.vue'
import NewChatMessageMedia from './messages/NewChatMessageMedia.vue'
import NewChatComposer from './composer/NewChatComposer.vue'
import NewChatAttachmentViewer from './NewChatAttachmentViewer.vue'

export default {
    name: 'NewChatMessenger',
    components: {
        vPreloader,
        NewChatMessageEvent,
        NewChatMessageText,
        NewChatMessageMedia,
        NewChatComposer,
        NewChatAttachmentViewer,
    },
    data() {
        return {
            viewerAttachment: null,
            currentPage:   1,
            hasMore:       true,
            isLoadingMore: false,
        }
    },
    computed: {
        ...mapGetters('newChat', ['messages', 'messagesRevision', 'isMessagesLoading', 'activeChat']),
        canCompose() {
            return this.activeChat && this.activeChat.can_assign === false
        },
    },
    created() {
        this._scrollHandler  = () => this._onScroll()
        this._prepending     = false
        this._ignoringScroll = false
        this._loadingMore    = false
    },
    beforeDestroy() {
        const el = this.$refs.list
        if (el) el.removeEventListener('scroll', this._scrollHandler)
    },
    watch: {
        // Initial load finished → attach scroll listener and scroll to bottom
        isMessagesLoading(val) {
            if (!val) {
                this.$nextTick(() => {
                    this._attachScrollListener()
                    this.scrollToBottom()
                })
            }
        },
        // New messages (send/receive via FCM) → scroll to bottom; skip when prepending older pages
        messages() {
            if (this._prepending || this.isMessagesLoading) return
            this.$nextTick(this.scrollToBottom)
        },
        // setMessages() was called (initial load or FCM refresh) → reset pagination
        messagesRevision() {
            this.currentPage = 1
            this.hasMore     = true
        },
    },
    methods: {
        ...mapActions('newChat', ['prependMessages']),

        scrollToBottom() {
            const el = this.$refs.list
            if (!el) return
            this._ignoringScroll = true
            el.scrollTop = el.scrollHeight
            this.$nextTick(() => { this._ignoringScroll = false })
        },

        _attachScrollListener() {
            const el = this.$refs.list
            if (!el || el._hasScrollListener) return
            el.addEventListener('scroll', this._scrollHandler)
            el._hasScrollListener = true
        },

        _onScroll() {
            if (this._ignoringScroll || this._loadingMore || !this.hasMore) return
            const el = this.$refs.list
            if (!el || el.scrollTop >= 80) return
            this._loadMoreMessages()
        },

        _loadMoreMessages() {
            const el = this.$refs.list
            if (!el) return
            const nextPage         = this.currentPage + 1
            const prevScrollHeight = el.scrollHeight

            this._loadingMore  = true
            this.isLoadingMore = true

            api.newChat.getMessages(this.activeChat.id, nextPage)
                .then(response => {
                    const msgs = Array.isArray(response) ? response : (response.messages || [])
                    const existingIds = new Set(this.messages.map(m => m.id))
                    const hasNew = msgs.some(m => !existingIds.has(m.id))

                    if (msgs.length === 0 || !hasNew) {
                        this.hasMore       = false
                        this._loadingMore  = false
                        this.isLoadingMore = false
                        return
                    }
                    // Reset flags BEFORE $nextTick so the spinner is gone from DOM
                    // when we measure scrollHeight — otherwise el.scrollHeight includes
                    // the spinner height and clamping scrollTop fires a cascade scroll event
                    this._prepending   = true
                    this._loadingMore  = false
                    this.isLoadingMore = false
                    this.prependMessages(msgs)
                    this.currentPage = nextPage
                    this.$nextTick(() => {
                        if (!el.isConnected) return
                        this._ignoringScroll = true
                        el.scrollTop = el.scrollHeight - prevScrollHeight
                        this._prepending = false
                        this.$nextTick(() => { this._ignoringScroll = false })
                    })
                })
                .catch(err => {
                    console.error('[NewChat] loadMore error:', err)
                    this._loadingMore  = false
                    this.isLoadingMore = false
                })
        },

        openViewer(attachment) {
            this.viewerAttachment = attachment
        },
    },
}
</script>

<style lang="scss">
@import '../../../../../gulp-src/app/scss/_variables';

.nc-messenger {
    flex: 1 1 auto;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    min-height: 0;

    &__empty {
        flex: 1 1 auto;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        color: $global-gray;
    }

    &__list {
        flex: 1 1 auto;
        overflow-y: auto;
        padding: 8px 0 30px;
        display: flex;
        flex-direction: column;
    }

    &__load-more {
        display: flex;
        justify-content: center;
        padding: 8px 0;
    }

    &__row {
        display: contents;
    }
}
</style>
